/**
 * Piezas compartidas entre InstitutionalDashboard.tsx y UnitDashboard.tsx --
 * indicadores de riesgo/cumplimiento, la lista accionable de "necesitan
 * atención" (actividades OBSERVADO hace mucho tiempo) y el gráfico de
 * evolución mensual. Ver src/dashboards.ts (backend) para el cálculo; todo
 * esto son banderas que ya existían en la base, solo faltaba mostrarlas
 * agregadas (pedido explícito del DPD).
 */
import { Link } from 'react-router-dom';
import type { AttentionRow, MonthlyEvolutionRow, RiskIndicators } from '../lib/queries';

const MONTH_LABEL = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function monthLabel(mes: string): string {
  const idx = Number(mes.slice(5, 7)) - 1;
  return MONTH_LABEL[idx] ?? mes;
}

export function RiskKpis({ riesgo }: { riesgo: RiskIndicators }) {
  const items: Array<{ n: number; l: string; tone?: 'warning' | 'critical' }> = [
    { n: riesgo.con_datos_sensibles, l: 'Con datos sensibles' },
    { n: riesgo.con_transferencia_internacional, l: 'Con transferencia internacional' },
    { n: riesgo.con_decision_automatizada, l: 'Con decisión automatizada' },
    { n: riesgo.observadas_hace_tiempo, l: 'Observadas hace +14 días', tone: riesgo.observadas_hace_tiempo > 0 ? 'critical' : undefined },
  ];
  return (
    <div className="kpis" style={{ marginBottom: 18 }}>
      {items.map((it) => (
        <div
          key={it.l}
          className="kpi"
          style={
            it.tone
              ? { borderColor: `var(--${it.tone})`, background: `var(--${it.tone}-weak)` }
              : undefined
          }
        >
          <div className="n" style={it.tone ? { color: `var(--${it.tone})` } : undefined}>
            {it.n}
          </div>
          <div className="l">{it.l}</div>
        </div>
      ))}
    </div>
  );
}

export function AttentionTable({ rows, showUnit }: { rows: AttentionRow[]; showUnit?: boolean }) {
  if (rows.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 12.5 }}>
        Ninguna actividad observada lleva más de 14 días sin corregir.
      </p>
    );
  }
  return (
    <div className="tablewrap" style={{ border: 0 }}>
      <table className="data">
        <thead>
          <tr>
            <th>Código</th>
            <th>Actividad</th>
            {showUnit && <th>Unidad</th>}
            <th>Días sin movimiento</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.ref_code ?? '—'}</td>
              <td>
                <Link to={`/actividades/${r.id}`}>{r.title}</Link>
              </td>
              {showUnit && <td>{r.unit_name}</td>}
              <td>
                <span className="pill pill--warning">{r.dias_sin_movimiento} días</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CHART_W = 600;
const CHART_H = 200;
const PAD_L = 32;
const PAD_B = 28;
const PAD_T = 12;
const PAD_R = 12;

export function MonthlyEvolutionChart({ data }: { data: MonthlyEvolutionRow[] }) {
  const plotW = CHART_W - PAD_L - PAD_R;
  const plotH = CHART_H - PAD_T - PAD_B;
  const maxVal = Math.max(1, ...data.map((d) => Math.max(d.creadas, d.aprobadas)));
  const ticks = 4;
  const groupW = plotW / data.length;
  const barW = Math.min(22, groupW / 2 - 4);

  const y = (v: number) => PAD_T + plotH - (v / maxVal) * plotH;

  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} style={{ width: '100%', height: 'auto', maxWidth: 640 }} role="img" aria-label="Actividades creadas y aprobadas por mes">
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const v = Math.round((maxVal / ticks) * i);
        const yy = y(v);
        return (
          <g key={i}>
            <line x1={PAD_L} x2={CHART_W - PAD_R} y1={yy} y2={yy} stroke="var(--border)" strokeWidth={1} />
            <text x={PAD_L - 6} y={yy + 3} textAnchor="end" fontSize={9} fill="var(--ink-muted)">
              {v}
            </text>
          </g>
        );
      })}

      {data.map((d, i) => {
        const gx = PAD_L + i * groupW;
        const cx = gx + groupW / 2 - barW / 2 - 2;
        const ax = gx + groupW / 2 + 2;
        return (
          <g key={d.mes}>
            <rect x={cx} y={y(d.creadas)} width={barW} height={PAD_T + plotH - y(d.creadas)} fill="var(--brand-gray)" rx={2} />
            <rect x={ax} y={y(d.aprobadas)} width={barW} height={PAD_T + plotH - y(d.aprobadas)} fill="var(--brand)" rx={2} />
            <text x={gx + groupW / 2} y={CHART_H - 8} textAnchor="middle" fontSize={10} fill="var(--ink-muted)">
              {monthLabel(d.mes)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function EvolutionLegend() {
  return (
    <div style={{ display: 'flex', gap: 16, fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 4 }}>
      <span>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--brand-gray)', borderRadius: 2, marginRight: 5 }} />
        Creadas
      </span>
      <span>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--brand)', borderRadius: 2, marginRight: 5 }} />
        Aprobadas
      </span>
    </div>
  );
}
